import { useEffect, useState } from "react";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useAuthStatus } from "../lib/useAuthStatus";
import {
  getWatchlist,
  addWatchlistItem,
  addWatchlistProduct,
  removeWatchlistItem,
} from "../lib/api";

/**
 * Lightweight save/unsave toggle for products + ingredients.
 *
 * Props:
 *   kind         "product" | "ingredient"
 *   targetId     The id used by the watchlist API (canonical product id /
 *                ingredient UUID — NOT a slug).
 *   labelHint    Optional name shown in toast copy.
 *   onAuthNeeded Optional callback invoked when a signed-out user clicks save.
 *   variant      "subtle" (default — pill, blends with content) | "ghost"
 *
 * The component fetches the current watchlist on mount to know whether the
 * item is already saved. We cache a session-local map on `window` so a page
 * with multiple watch buttons doesn't issue a request per button.
 */

const SESSION_CACHE_KEY = "__medskinWatchCache";

function readCache() {
  if (typeof window === "undefined") return null;
  return window[SESSION_CACHE_KEY] || null;
}
function setCache(cache) {
  if (typeof window === "undefined") return;
  window[SESSION_CACHE_KEY] = cache;
}

async function loadWatchSet({ user }) {
  if (!user) return { products: new Set(), ingredients: new Set() };
  const cached = readCache();
  if (cached) return cached;
  try {
    const res = await getWatchlist();
    const products = new Set(
      (res.items || [])
        .filter((i) => i.kind === "product")
        .map((i) => i.product_id),
    );
    const ingredients = new Set(
      (res.items || [])
        .filter((i) => i.kind === "ingredient")
        .map((i) => i.ingredient_id),
    );
    const cache = { products, ingredients };
    setCache(cache);
    return cache;
  } catch {
    return { products: new Set(), ingredients: new Set() };
  }
}

export default function WatchButton({
  kind,
  targetId,
  labelHint,
  onAuthNeeded,
  className = "",
}) {
  const { user } = useAuth();
  const { authEnabled } = useAuthStatus();
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Initial state — derive from cached watchlist.
  useEffect(() => {
    let cancelled = false;
    if (!user || !targetId) {
      setSaved(false);
      setHydrated(true);
      return;
    }
    loadWatchSet({ user }).then((cache) => {
      if (cancelled) return;
      const set = kind === "product" ? cache.products : cache.ingredients;
      setSaved(set.has(targetId));
      setHydrated(true);
    });
    return () => { cancelled = true; };
  }, [user, kind, targetId]);

  const updateCache = (mutator) => {
    const cache = readCache();
    if (!cache) return;
    mutator(cache);
    setCache(cache);
  };

  const handleClick = async () => {
    if (!user) {
      onAuthNeeded?.();
      return;
    }
    if (!targetId) return;
    const next = !saved;
    setSaved(next);              // optimistic
    setLoading(true);
    try {
      if (next) {
        if (kind === "product") {
          await addWatchlistProduct(targetId);
          updateCache((c) => c.products.add(targetId));
        } else {
          await addWatchlistItem(targetId);
          updateCache((c) => c.ingredients.add(targetId));
        }
        toast.success(`Saved${labelHint ? ` · ${labelHint}` : ""}`, {
          description: "Find it any time on your watchlist.",
        });
      } else {
        await removeWatchlistItem(targetId, { kind });
        updateCache((c) => {
          (kind === "product" ? c.products : c.ingredients).delete(targetId);
        });
        toast.message("Removed from watchlist", {
          description: "It's no longer tracked — re-save anytime from this page.",
        });
      }
    } catch (e) {
      setSaved(!next);            // rollback
      const msg = e?.response?.data?.detail || "Couldn't update your watchlist.";
      toast.error("Watchlist couldn't update", {
        description: typeof msg === "string" ? msg : "Check your connection and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // When auth is fully disabled, render a muted disabled affordance so
  // we don't tease a feature that can't actually save anything.
  if (authEnabled === false) {
    return (
      <button
        type="button"
        disabled
        data-testid={`watch-btn-${kind}-disabled`}
        title="Sign-in launches soon — saving will arrive with it."
        className={`inline-flex items-center gap-2 h-9 px-3 rounded-full border border-surface-line text-[13px] font-semibold text-ink-mute bg-cream-100 cursor-not-allowed opacity-70 ${className}`}
      >
        <Bookmark className="w-4 h-4" /> Save
      </button>
    );
  }

  const Icon = saved ? BookmarkCheck : Bookmark;
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading || !hydrated}
      aria-pressed={saved}
      data-testid={`watch-btn-${kind}`}
      className={
        `inline-flex items-center gap-2 h-9 px-3 rounded-full border text-[13px] font-semibold transition-all ` +
        (saved
          ? "bg-ink text-cream-50 border-ink hover:bg-ink-soft"
          : "bg-white text-ink border-surface-line hover:border-ink/40 hover:bg-cream-100") +
        ` disabled:opacity-60 disabled:cursor-wait ${className}`
      }
    >
      <Icon className="w-4 h-4" />
      {saved ? "Saved" : "Save"}
    </button>
  );
}
